import { financialService } from "../../services/financialService";
import { byText, type RegisterConfig } from "../register/registerLogic";

/**
 * Every finance report the server issues as CSV and PDF, in one list: what
 * each shows, which dates it takes, and where it lives in Finance.
 */

export type ReportCovers = "period" | "period_end" | "as_at" | "today";
export type ReportFormat = "csv" | "pdf";
export interface ReportDates {
  start: string;
  end: string;
  asOf: string;
}

export interface LibraryReport {
  key: string;
  name: string;
  group: string;
  description: string;
  covers: ReportCovers;
  /** The Finance page where it can be read on screen. */
  href: string;
  /** Needs the audit-log capability as well as report access. */
  audit?: boolean;
  download: (format: ReportFormat, dates: ReportDates) => unknown;
}

export const REPORT_GROUPS = ["Statements", "Fees & receivables", "Suppliers & payables", "Cash", "Grants & assets", "Audit"] as const;

const period = ({ start, end }: ReportDates) => ({ start_date: start, end_date: end });

export const LIBRARY_REPORTS: LibraryReport[] = [
  {
    key: "trial_balance", name: "Trial balance", group: "Statements", covers: "period_end", href: "/financial/trial-balance",
    description: "Every account's debit or credit balance, proving the books balance.",
    download: (format, dates) => financialService.downloadAccountingReport("trial_balance", format, period(dates)),
  },
  {
    key: "income_expenditure", name: "Statement of income and expenditure", group: "Statements", covers: "period", href: "/financial/financial-statements",
    description: "What the school earned and spent over the period, and the surplus or deficit.",
    download: (format, dates) => financialService.downloadFinancialStatement("income_expenditure", format, period(dates)),
  },
  {
    key: "financial_position", name: "Statement of financial position", group: "Statements", covers: "period_end", href: "/financial/financial-statements",
    description: "What the school owns and owes, and its accumulated fund.",
    download: (format, dates) => financialService.downloadFinancialStatement("financial_position", format, period(dates)),
  },
  {
    key: "changes_in_equity", name: "Statement of changes in equity", group: "Statements", covers: "period", href: "/financial/financial-statements",
    description: "How the accumulated fund moved over the period.",
    download: (format, dates) => financialService.downloadFinancialStatement("changes_in_equity", format, period(dates)),
  },
  {
    key: "fee_collection", name: "Fee collection", group: "Fees & receivables", covers: "period", href: "/financial/statements?view=income",
    description: "Fees received in the period, by fee.",
    download: (format, dates) => financialService.downloadFeeCollectionReport(format, period(dates)),
  },
  {
    key: "outstanding_fees", name: "Outstanding fees", group: "Fees & receivables", covers: "today", href: "/financial/statements?view=arrears",
    description: "Every unpaid fee invoice, with the learner and class.",
    download: (format) => financialService.downloadOutstandingFeesReport(format),
  },
  {
    key: "receivables_ageing", name: "Receivables ageing", group: "Fees & receivables", covers: "as_at", href: "/financial/statements?view=ageing",
    description: "Unpaid fee invoices by how long they are overdue.",
    download: (format, { asOf }) => financialService.downloadReceivablesAgeing(format, { as_of: asOf }),
  },
  {
    key: "receivables_control", name: "Student ledger reconciliation", group: "Fees & receivables", covers: "as_at", href: "/financial/statements?view=reconciliations",
    description: "Proof that the student accounts agree with the receivables control account.",
    download: (format, { asOf }) => financialService.downloadReceivablesControlReconciliation(format, { as_of: asOf }),
  },
  {
    key: "payables_ageing", name: "Payables ageing", group: "Suppliers & payables", covers: "as_at", href: "/financial/statements?view=ageing",
    description: "Approved supplier bills not yet paid, by how long they are overdue.",
    download: (format, { asOf }) => financialService.downloadPayablesAgeing(format, { as_of: asOf }),
  },
  {
    key: "payables_control", name: "Supplier ledger reconciliation", group: "Suppliers & payables", covers: "as_at", href: "/financial/statements?view=reconciliations",
    description: "Proof that the supplier accounts agree with the payables control account.",
    download: (format, { asOf }) => financialService.downloadPayablesControlReconciliation(format, { as_of: asOf }),
  },
  {
    key: "cash_flow", name: "Cash flow", group: "Cash", covers: "period", href: "/financial/statements?view=income",
    description: "Money in and money out over the period, by source.",
    download: (format, dates) => financialService.downloadCashFlowReport(format, period(dates)),
  },
  {
    key: "grant_summary", name: "Grant summary", group: "Grants & assets", covers: "today", href: "/financial/statements?view=grants",
    description: "Each donor grant: committed, received, spent and unspent.",
    download: (format) => financialService.downloadGrantSummaryReport(format),
  },
  {
    key: "fixed_assets", name: "Fixed asset register", group: "Grants & assets", covers: "today", href: "/financial/statements?view=grants",
    description: "Capitalised assets at cost, depreciation and book value.",
    download: (format) => financialService.downloadFixedAssetRegisterReport(format),
  },
  {
    key: "audit_approvals", name: "Audit & approval trail", group: "Audit", covers: "period", href: "/financial/statements?view=audit", audit: true,
    description: "Every submission, approval, rejection and reversal on finance documents.",
    download: (format, dates) => financialService.downloadAuditApprovalReport(format, period(dates)),
  },
];

/** The reports this person may see. */
export const reportsFor = (canViewAuditLogs: boolean) => LIBRARY_REPORTS.filter((report) => !report.audit || canViewAuditLogs);

/** The dates a report will use, in words. `day` formats one yyyy-mm-dd. */
export function coversText(report: Pick<LibraryReport, "covers">, dates: ReportDates, day: (value: string) => string) {
  switch (report.covers) {
    case "period":
      return `${day(dates.start)} – ${day(dates.end)}`;
    case "period_end":
      return `As at ${day(dates.end)}`;
    case "as_at":
      return `As at ${day(dates.asOf)}`;
    default:
      return "As things stand today";
  }
}

const groupRank = (report: LibraryReport) => REPORT_GROUPS.indexOf(report.group as (typeof REPORT_GROUPS)[number]);

export const libraryConfig: RegisterConfig<LibraryReport> = {
  search: (report) => [report.name, report.description, report.group].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "period", label: "For a period", match: (report) => report.covers === "period" || report.covers === "period_end" },
    { key: "as_at", label: "On a date", match: (report) => report.covers === "as_at" },
    { key: "today", label: "As of today", match: (report) => report.covers === "today" },
  ],
  category: { label: "Area", value: (report) => report.group },
  sorts: [
    { key: "group", label: "By area", compare: (a, b) => groupRank(a) - groupRank(b) },
    { key: "name", label: "Name (A–Z)", compare: byText<LibraryReport>((report) => report.name) },
  ],
};
